import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

const ROLE_PATH = {
  family_member: "/family",
  care_worker: "/visits",
  bank_worker: "/visits",
};

export default function ChangePassword() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const isForced = !!user?.force_password_change;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next === current) { setError("New password must be different from your current password."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setDone(true);
      // Refresh /auth/me so force_password_change flag clears and use FRESH user
      // (the stale `user` in closure still has force_password_change=true)
      const fresh = refreshMe ? await refreshMe() : user;
      setTimeout(() => {
        // Next: if MFA not yet enabled for a non-family role, push to MFA setup
        if (fresh?.mfa_setup_required || (fresh && !fresh.mfa_enabled && !["family_member"].includes(fresh.role))) {
          nav("/settings/mfa", { replace: true });
        } else {
          nav(ROLE_PATH[fresh?.role] || "/dashboard", { replace: true });
        }
      }, 1400);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  const strength = scoreStrength(next);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-6 cc-glow" data-testid="change-password-card">
        <div className="flex items-center gap-2 text-[#0D7377]">
          <KeyRound className="h-6 w-6" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">{isForced ? "Set your new password" : "Change password"}</h1>
        </div>
        {isForced && (
          <p className="mt-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="forced-change-banner">
            For security, you must set a new password before continuing. Your one-time password is no longer valid afterwards.
          </p>
        )}
        {done && (
          <p className="mt-3 text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="change-password-success">
            Password updated. Redirecting…
          </p>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="change-password-form">
          <PwField label="Current password" value={current} onChange={setCurrent} show={show} setShow={setShow} testid="current-password-input" autoFocus />
          <PwField label="New password" value={next} onChange={setNext} show={show} setShow={setShow} testid="new-password-input" />
          <StrengthBar score={strength.score} hint={strength.hint} />
          <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={show} setShow={setShow} testid="confirm-password-input" />
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-password-error">{error}</div>}
          <button type="submit" disabled={busy || done || strength.score < 3} data-testid="change-password-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] disabled:opacity-50 text-white font-semibold py-2.5 rounded-md min-h-[44px]">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isForced ? "Set password & continue" : "Update password"}
          </button>
        </form>

        <ul className="mt-5 text-xs text-slate-600 space-y-0.5">
          <li>• Minimum 12 characters</li>
          <li>• Must mix at least 3 of: upper case, lower case, digits, symbols</li>
          <li>• Avoid words tied to your name, email or organisation</li>
        </ul>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, show, setShow, testid, autoFocus }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          autoFocus={autoFocus}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1 w-full px-3 py-2.5 pr-10 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white"
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute top-1/2 right-2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-800" aria-label={show ? "Hide password" : "Show password"}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function StrengthBar({ score, hint }) {
  const colors = ["bg-red-400", "bg-red-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];
  return (
    <div data-testid="password-strength">
      <div className="grid grid-cols-4 gap-1 h-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`rounded ${i < score ? colors[score] : "bg-slate-200"}`} />
        ))}
      </div>
      <p className="mt-1 text-[11px] text-slate-500">{hint}</p>
    </div>
  );
}

function scoreStrength(pw) {
  if (!pw) return { score: 0, hint: "" };
  const classes = [/[A-Z]/.test(pw), /[a-z]/.test(pw), /\d/.test(pw), /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (classes >= 3) score += 1;
  if (pw.length >= 16 && classes === 4) score += 1;
  const hints = ["", "Weak — use a longer password.", "Acceptable — mix more character types.", "Strong.", "Excellent."];
  return { score, hint: hints[score] };
}
